import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { ArrowRight, Sparkles, Zap, ShieldCheck, Palette, Truck, Users, Award, Clock, ThumbsUp, ShoppingBag, HeadphonesIcon as Headset } from "lucide-react";
import usePageTitle from "@/hooks/use-page-title";
import HomeHero from "@/components/public/HomeHero";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GROUP_ACCENTS = {
  "Business Cards": { from: "from-orange-500", to: "to-red-600", ring: "ring-orange-200" },
  "Business Stationery": { from: "from-blue-600", to: "to-indigo-800", ring: "ring-blue-200" },
  "Marketing Materials": { from: "from-emerald-500", to: "to-teal-700", ring: "ring-emerald-200" },
  "Label & Packaging": { from: "from-amber-500", to: "to-orange-700", ring: "ring-amber-200" },
  "Indoor & Outdoor": { from: "from-purple-600", to: "to-pink-700", ring: "ring-purple-200" },
  "Personalised & Gifting": { from: "from-rose-500", to: "to-fuchsia-700", ring: "ring-rose-200" },
};

const WHY_CHOOSE = [
  { icon: Award, title: "Premium Quality", desc: "Top-grade materials & advanced printing tech." },
  { icon: Truck, title: "Fast Delivery", desc: "On-time delivery across India with reliable service." },
  { icon: Palette, title: "Custom Design Support", desc: "Expert designers to bring your ideas to life." },
  { icon: ShoppingBag, title: "Bulk Orders", desc: "Best prices & dedicated support for bulk needs." },
];

const SERVICES = [
  { icon: Palette, title: "Flex & Vinyl Printing", desc: "Large-format signage, banners & outdoor branding" },
  { icon: Sparkles, title: "ACP & Glow Signage", desc: "Premium building & shop signage with LED backlit" },
  { icon: Zap, title: "Digital Print & Offset", desc: "Business cards, brochures, flyers, standees" },
  { icon: Users, title: "Corporate Branding", desc: "T-shirts, mugs, corporate gifts, ID cards" },
  { icon: Truck, title: "Vehicle Branding", desc: "Full-wrap, half-wrap, one-way vision & fleet decals" },
  { icon: ShieldCheck, title: "Installation & Delivery", desc: "PAN-Navi Mumbai installation with warranty" },
];

const WHY = [
  { icon: Award, title: "Premium Quality", desc: "Every job is quality-checked before dispatch." },
  { icon: Clock, title: "On-Time Delivery", desc: "Deadlines respected. Live tracking on every order." },
  { icon: ThumbsUp, title: "Customer-First", desc: "Fair pricing. Transparent updates. Zero surprises." },
];

const STATS = [
  ["1,000+", "Happy Clients"],
  ["7+", "Years of Craft"],
  ["50+", "Product Categories"],
  ["24/7", "Support"],
];

const FALLBACK_TESTIMONIALS = [
  { name: "Rakesh Sharma", role: "Founder, Sharma Traders", body: "Brothers Advertising handled all our shop signage. Precision work, on-time, and stunning finish.", rating: 5 },
  { name: "Priya Menon", role: "Menon Bakery", body: "Their flex banners look brand new even after two monsoons. Absolutely dependable.", rating: 5 },
  { name: "Deepak Rao", role: "Rao Electronics", body: "From visiting cards to LED sign boards — every job was flawless. Our go-to print partner.", rating: 5 },
];

export default function Home() {
  usePageTitle();
  const [clients, setClients] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [catalog, setCatalog] = useState([]);
  useEffect(() => {
    fetch(`${API}/media?category=client`).then((r) => r.json()).then(setClients).catch(() => setClients([]));
    fetch(`${API}/testimonials?active_only=true`).then((r) => r.json())
      .then((rows) => setTestimonials((rows && rows.length) ? rows : FALLBACK_TESTIMONIALS))
      .catch(() => setTestimonials(FALLBACK_TESTIMONIALS));
    fetch(`${API}/catalog?active_only=true`).then((r) => r.json()).then(setCatalog).catch(() => setCatalog([]));
  }, []);

  const categories = Array.from(new Set(catalog.map((c) => c.group))).map((g) => ({
    name: g,
    count: catalog.filter((c) => c.group === g).length,
    accent: GROUP_ACCENTS[g] || { from: "from-zinc-500", to: "to-zinc-700", ring: "ring-zinc-200" },
    sample: catalog.find((c) => c.group === g),
  }));
  const trending = catalog.filter((c) => c.trending).slice(0, 8);
  const bestSellers = catalog.filter((c) => c.best_seller).slice(0, 6);
  return (
    <div>
      {/* Hero — admin-editable slides */}
      <HomeHero />

      {/* Shop By Category */}
      <section className="py-16 max-w-7xl mx-auto px-4 lg:px-8" data-testid="shop-by-category">
        <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
          <div>
            <div className="overline mb-1">Shop</div>
            <h2 className="font-display text-4xl md:text-5xl font-black tracking-tighter">Shop By Category</h2>
          </div>
          <Link to="/products" className="text-sm font-bold text-[#FF6B00] hover:underline inline-flex items-center gap-1">
            View all products <ArrowRight size={14} />
          </Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {categories.map((c) => (
            <Link
              key={c.name}
              to={`/products?g=${encodeURIComponent(c.name)}`}
              className="group relative overflow-hidden rounded-2xl aspect-[3/4] block hover:-translate-y-1 transition-transform"
              data-testid={`category-${c.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            >
              {/* Product image layer */}
              {c.sample?.image_url ? (
                <img
                  src={c.sample.image_url}
                  alt={c.name}
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  onError={(e) => { e.currentTarget.style.display = "none"; }}
                />
              ) : null}
              {/* Colour wash on top of image for readability + brand accent */}
              <div className={`absolute inset-0 bg-gradient-to-br ${c.accent.from} ${c.accent.to} ${c.sample?.image_url ? "opacity-70 mix-blend-multiply" : ""} transition-opacity group-hover:opacity-90`} />
              {/* Bottom gradient for text legibility */}
              <div className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
              <div className="absolute inset-0 flex flex-col justify-end p-4 text-white">
                <div className="text-[10px] uppercase tracking-widest font-bold text-white/80">{c.count} products</div>
                <div className="font-display text-xl font-black leading-tight mt-1 drop-shadow">{c.name}</div>
                <div className="mt-2 text-xs font-bold inline-flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  Explore <ArrowRight size={12} />
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Trending Products */}
      {trending.length > 0 && (
        <section className="py-14 bg-gradient-to-b from-orange-50/50 to-white">
          <div className="max-w-7xl mx-auto px-4 lg:px-8">
            <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
              <div>
                <div className="overline text-[#FF6B00]">🔥 Right Now</div>
                <h2 className="font-display text-4xl md:text-5xl font-black tracking-tighter">Trending Products</h2>
              </div>
              <Link to="/products" className="text-sm font-bold text-[#FF6B00] hover:underline inline-flex items-center gap-1">
                See all <ArrowRight size={14} />
              </Link>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="trending-products">
              {trending.map((p) => {
                const acc = GROUP_ACCENTS[p.group] || { from: "from-zinc-500", to: "to-zinc-700" };
                const startsFrom = Math.round((p.quantity_tiers?.[0]?.unit_price ?? p.base_price) * (p.min_quantity || 1));
                return (
                  <Link key={p.id} to={`/products/${p.id}`} className="group rounded-2xl overflow-hidden border-2 border-zinc-200 hover:border-[#FF6B00] hover:shadow-xl hover:-translate-y-1 transition-all bg-white" data-testid={`trending-${p.id}`}>
                    {p.image_url ? (
                      <div className="aspect-square relative overflow-hidden">
                        <img src={p.image_url} alt={p.name} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform" onError={(e) => { e.target.style.display="none"; }} />
                        <div className="absolute top-3 left-3 text-[10px] uppercase font-bold text-white bg-[#FF6B00] px-2 py-0.5 rounded-full">Trending</div>
                      </div>
                    ) : (
                      <div className={`aspect-square bg-gradient-to-br ${acc.from} ${acc.to} flex items-center justify-center p-4 relative`}>
                        <div className="text-white font-display text-xl md:text-2xl font-black text-center leading-tight tracking-tight">{p.name}</div>
                        <div className="absolute top-3 left-3 text-[10px] uppercase font-bold text-white bg-black/30 px-2 py-0.5 rounded backdrop-blur">Trending</div>
                      </div>
                    )}
                    <div className="p-4">
                      <div className="font-bold text-sm truncate group-hover:text-[#FF6B00]">{p.name}</div>
                      <div className="text-[10px] text-zinc-500">{p.category}</div>
                      <div className="mt-2 text-xs text-zinc-500">Starts from</div>
                      <div className="font-mono text-[#FF6B00] font-black text-lg">₹{startsFrom.toLocaleString("en-IN")}</div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Why Choose Us — Shivam-style compact strip */}
      <section className="py-12 max-w-7xl mx-auto px-4 lg:px-8">
        <div className="text-center mb-8">
          <div className="overline mb-1">Why Us</div>
          <h2 className="font-display text-3xl md:text-4xl font-black tracking-tighter">Why Choose Brothers Advertising?</h2>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {WHY_CHOOSE.map((w) => (
            <div key={w.title} className="p-6 rounded-2xl border border-zinc-200 bg-white text-center hover:border-[#FF6B00] hover:shadow-lg transition-all group">
              <div className="w-14 h-14 mx-auto mb-3 rounded-full bg-orange-50 flex items-center justify-center group-hover:bg-[#FF6B00] transition-colors">
                <w.icon size={22} className="text-[#FF6B00] group-hover:text-white transition-colors" />
              </div>
              <div className="font-bold text-lg">{w.title}</div>
              <div className="text-xs text-zinc-600 mt-1">{w.desc}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Best Sellers strip */}
      {bestSellers.length > 0 && (
        <section className="py-14 max-w-7xl mx-auto px-4 lg:px-8">
          <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
            <div>
              <div className="overline text-emerald-700">⭐ Customer Favourites</div>
              <h2 className="font-display text-3xl md:text-4xl font-black tracking-tighter">Most Selling Products</h2>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="best-sellers">
            {bestSellers.map((p) => {
              const acc = GROUP_ACCENTS[p.group] || { from: "from-zinc-500", to: "to-zinc-700" };
              const startsFrom = Math.round((p.quantity_tiers?.[0]?.unit_price ?? p.base_price) * (p.min_quantity || 1));
              return (
                <Link key={p.id} to={`/products/${p.id}`} className="group rounded-xl overflow-hidden border border-zinc-200 hover:border-[#FF6B00] transition-all bg-white block" data-testid={`bestseller-${p.id}`}>
                  {p.image_url ? (
                    <div className="aspect-square relative overflow-hidden">
                      <img src={p.image_url} alt={p.name} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform" onError={(e) => { e.target.style.display="none"; }} />
                    </div>
                  ) : (
                    <div className={`aspect-square bg-gradient-to-br ${acc.from} ${acc.to} flex items-center justify-center p-3`}>
                      <div className="text-white font-bold text-sm text-center leading-tight">{p.name}</div>
                    </div>
                  )}
                  <div className="p-2.5">
                    <div className="font-semibold text-xs truncate">{p.name}</div>
                    <div className="font-mono text-[#FF6B00] font-black text-sm">₹{startsFrom.toLocaleString("en-IN")}</div>
                  </div>
                </Link>
              );
            })}
          </div>
        </section>
      )}

      {/* Why Choose Us */}

      <section className="py-20 bg-zinc-900 text-white">
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <div className="text-center mb-12">
            <div className="text-xs uppercase tracking-[0.3em] text-[#FF6B00] font-bold mb-2">Why Brothers Advertising</div>
            <h2 className="font-display text-4xl md:text-5xl font-black tracking-tighter">Craft. Speed. Reliability.</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {WHY.map((w) => (
              <div key={w.title} className="p-8 rounded-2xl bg-white/5 border border-white/10 backdrop-blur">
                <w.icon size={28} className="text-[#FF6B00] mb-4" />
                <div className="font-bold text-xl mb-2">{w.title}</div>
                <div className="text-sm text-zinc-400">{w.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Clients — sliding logos strip */}
      <section className="py-16 border-t border-b border-zinc-200 bg-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 lg:px-8 mb-8">
          <div className="text-center">
            <div className="overline mb-2">Trusted By</div>
            <h2 className="font-display text-4xl md:text-5xl font-black tracking-tighter">Our Clients</h2>
            <p className="mt-3 text-zinc-600 text-sm max-w-xl mx-auto">
              From neighbourhood shops to national brands — hundreds of businesses run their signage & print through us.
            </p>
          </div>
        </div>
        {clients.length > 0 ? (
          <div className="relative">
            <div className="marquee flex gap-14 items-center py-4" data-testid="clients-marquee">
              {[...clients, ...clients].map((c, i) => (
                <div key={`${c.id}-${i}`} className="flex-shrink-0 h-20 md:h-24 flex items-center justify-center">
                  <img src={`${API}/media/${c.id}`} alt={c.label || "Client"}
                    className="max-h-full max-w-[180px] object-contain grayscale hover:grayscale-0 transition-all duration-300 opacity-70 hover:opacity-100" />
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="max-w-4xl mx-auto text-center text-sm text-zinc-500 py-8">
            Client showcase coming soon. Are you one of our clients? <Link to="/contact" className="text-[#FF6B00] font-semibold hover:underline">Say hi</Link>.
          </div>
        )}
      </section>

      {/* Testimonials */}
      <section className="py-20 max-w-7xl mx-auto px-4 lg:px-8">
        <div className="text-center mb-12">
          <div className="overline mb-2">Kind Words</div>
          <h2 className="font-display text-4xl md:text-5xl font-black tracking-tighter">Trusted by growing brands</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {testimonials.map((t, i) => (
            <div key={t.id || t.name || i} className="p-6 rounded-2xl bg-white border border-zinc-200 shadow-sm">
              <div className="text-[#FF6B00] text-lg mb-3">{"★".repeat(t.rating || 5)}</div>
              <div className="text-sm text-zinc-700 leading-relaxed mb-4">"{t.body}"</div>
              <div className="border-t border-zinc-200 pt-3">
                <div className="font-bold text-sm">{t.name}</div>
                <div className="text-xs text-zinc-500">{t.role}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-r from-[#FF6B00] to-orange-500 text-white">
        <div className="max-w-4xl mx-auto text-center px-4">
          <h2 className="font-display text-4xl md:text-5xl font-black tracking-tighter mb-4">Ready to make your brand shine?</h2>
          <p className="text-lg text-white/90 mb-8 max-w-2xl mx-auto">
            Send us your requirement — we'll reply within 2 business hours with a custom quotation.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link to="/contact" className="inline-flex items-center gap-2 bg-white text-[#FF6B00] font-bold px-6 py-3">Request Quote <ArrowRight size={14} /></Link>
            <a href="https://wa.me/918070970980" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 border border-white text-white font-bold px-6 py-3 hover:bg-white/10">Chat on WhatsApp</a>
          </div>
        </div>
      </section>
    </div>
  );
}
